<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
      <div
        v-if="!tenantAdmin"
        class="rounded-lg border border-amber-400/35 bg-amber-500/10 text-amber-100/95 px-4 py-3 text-sm"
      >
        Apenas administradores do ambiente podem gerir agendamentos.
      </div>

      <template v-else>
        <div class="mb-6">
          <h1 class="text-3xl font-bold text-slate-100">Agendamentos</h1>
          <p class="text-slate-400 mt-1 text-sm max-w-3xl">
            Lista de todos os agendamentos cadastrados. Você pode ativar ou desativar a execução de um agendamento.
          </p>
        </div>

        <button
          type="button"
          class="sm:hidden mb-2 w-full flex items-center justify-between gap-2 rounded-xl border border-cyan-400/25 bg-slate-900/60 px-4 py-3 text-left text-sm font-medium text-slate-100 shadow-sm shadow-black/20"
          :aria-expanded="scheduleFiltersExpanded ? 'true' : 'false'"
          aria-controls="agent-schedules-filters-panel"
          @click="scheduleFiltersOpen = !scheduleFiltersOpen"
        >
          <span>Filtros</span>
          <svg
            class="w-5 h-5 text-cyan-300 shrink-0 transition-transform duration-200"
            :class="{ 'rotate-180': scheduleFiltersOpen }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div
          id="agent-schedules-filters-panel"
          v-show="scheduleFiltersExpanded"
          class="mb-6 rounded-xl border border-cyan-400/25 bg-slate-900/60 p-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4 w-full"
        >
          <div class="flex flex-col gap-2 sm:flex-row sm:gap-3 sm:items-end min-w-0">
            <div class="min-w-0 w-full sm:w-[min(22rem,100%)] sm:min-w-[18rem]">
              <label class="block text-[10px] uppercase tracking-wide text-white/90 mb-0.5">Agente (executor)</label>
              <select
                v-model="filters.agentId"
                class="agent-schedules-filter-control w-full px-2.5 py-1.5 rounded-md border border-slate-600 bg-slate-900/70 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
              >
                <option value="">Todos</option>
                <option v-for="a in executorOptions" :key="a.id" :value="String(a.id)">{{ a.name }}</option>
              </select>
            </div>
            <div class="min-w-0 w-full sm:w-[7.5rem] shrink-0">
              <label class="block text-[10px] uppercase tracking-wide text-white/90 mb-0.5">Ativo</label>
              <select
                v-model="filters.enabled"
                class="agent-schedules-filter-control w-full px-2.5 py-1.5 rounded-md border border-slate-600 bg-slate-900/70 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
              >
                <option value="all">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 shrink-0 justify-end">
            <button
              type="button"
              class="px-3 py-1.5 rounded-md bg-cyan-500/20 border border-cyan-400/50 text-cyan-100 text-xs font-medium hover:bg-cyan-500/30 disabled:opacity-50"
              :disabled="listLoading"
              @click="applyFilters"
            >
              Aplicar filtros
            </button>
            <button
              type="button"
              class="px-3 py-1.5 rounded-md border border-slate-600 text-slate-300 text-xs hover:bg-slate-800/80 disabled:opacity-50"
              :disabled="listLoading"
              @click="clearFilters"
            >
              Limpar
            </button>
          </div>
        </div>

        <div v-if="loadError" class="mb-4 p-3 rounded-lg border border-red-400/40 bg-red-950/30 text-red-200 text-sm">
          {{ loadError }}
        </div>

        <div class="rounded-xl border border-cyan-400/20 bg-slate-900/50 overflow-hidden">
          <!-- Mobile: cartões -->
          <div class="sm:hidden">
            <div v-if="listLoading" class="px-4 py-10 text-center text-slate-500 text-sm">A carregar…</div>
            <div v-else-if="!rows.length" class="px-4 py-10 text-center text-slate-500 text-sm">
              Nenhum agendamento encontrado com estes filtros.
            </div>
            <div v-else class="divide-y divide-slate-700/70">
              <div
                v-for="row in rows"
                :key="row.id"
                class="p-4 bg-slate-900/30 transition hover:bg-cyan-400/5"
              >
                <div class="grid grid-cols-[minmax(0,1fr)_7.5rem] items-center gap-3">
                  <div class="flex items-center gap-2 min-w-0">
                    <span
                      class="text-xs font-semibold shrink-0"
                      :class="row.enabled ? 'text-emerald-300/90' : 'text-slate-500'"
                    >
                      {{ row.enabled ? 'Ativo' : 'Inativo' }}
                    </span>
                    <span class="text-xs text-slate-500 font-mono shrink-0">#{{ row.id }}</span>
                  </div>
                  <button
                    type="button"
                    class="w-full px-2 py-1.5 rounded-md text-xs font-medium border text-center transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:pointer-events-none"
                    :class="
                      row.enabled
                        ? 'border-red-400/45 bg-red-500/10 text-red-100 hover:bg-red-500/20 focus:ring-red-400/45'
                        : 'border-emerald-500/45 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20 focus:ring-emerald-400/40'
                    "
                    :disabled="togglingId === row.id"
                    :aria-busy="togglingId === row.id"
                    @click="onToggleEnabled(row, !row.enabled)"
                  >
                    {{ row.enabled ? 'Inativar' : 'Ativar' }}
                  </button>
                </div>
                <div class="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div class="text-slate-400">Agente</div>
                  <div class="text-slate-100 font-medium text-right break-words">{{ row.agent.name }}</div>
                  <div class="text-slate-400">Especialidade</div>
                  <div class="text-slate-100 font-medium text-right">{{ specialtyLabel(row.agent.specialty) }}</div>
                  <div class="text-slate-400">Agente ativo</div>
                  <div class="text-right" :class="row.agent.active ? 'text-emerald-300/90 font-medium' : 'text-slate-500'">
                    {{ row.agent.active ? 'Sim' : 'Não' }}
                  </div>
                  <div class="text-slate-400">Descrição</div>
                  <div class="text-slate-100 font-medium text-right break-words">{{ row.description || '—' }}</div>
                  <div class="text-slate-400">Recorrência</div>
                  <div class="text-slate-300 text-right leading-snug">{{ scheduleSummary(row.schedule) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop: tabela -->
          <div class="hidden sm:block overflow-x-auto">
            <table class="min-w-full text-sm text-left text-slate-200">
              <thead class="text-[11px] uppercase text-white border-b border-slate-700 bg-slate-900/80">
                <tr>
                  <th colspan="2" class="w-[1%] px-3 py-2 font-medium whitespace-nowrap text-left">
                    Agendamento
                  </th>
                  <th class="px-3 py-2 font-medium">Agente</th>
                  <th class="px-3 py-2 font-medium">Especialidade</th>
                  <th class="px-3 py-2 font-medium">Agente ativo</th>
                  <th class="px-3 py-2 font-medium">Descrição</th>
                  <th class="px-3 py-2 font-medium whitespace-nowrap">Recorrência</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="listLoading" class="border-b border-slate-800">
                  <td colspan="7" class="px-3 py-8 text-center text-slate-500">A carregar…</td>
                </tr>
                <tr v-else-if="!rows.length" class="border-b border-slate-800">
                  <td colspan="7" class="px-3 py-8 text-center text-slate-500">
                    Nenhum agendamento encontrado com estes filtros.
                  </td>
                </tr>
                <tr
                  v-for="row in rows"
                  v-else
                  :key="row.id"
                  class="border-b border-slate-800/80 hover:bg-cyan-400/10"
                >
                  <td colspan="2" class="w-[1%] whitespace-nowrap px-3 py-2 align-middle">
                    <div class="inline-grid grid-cols-[3rem_5rem] items-center gap-2 max-w-full">
                      <span
                        class="min-w-0 text-xs font-medium truncate"
                        :class="row.enabled ? 'text-emerald-300/90' : 'text-slate-500'"
                      >
                        {{ row.enabled ? 'Ativo' : 'Inativo' }}
                      </span>
                      <button
                        type="button"
                        class="w-full px-2 py-1 rounded-md text-xs font-medium border text-center transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:pointer-events-none"
                        :class="
                          row.enabled
                            ? 'border-red-400/45 bg-red-500/10 text-red-100 hover:bg-red-500/20 focus:ring-red-400/45'
                            : 'border-emerald-500/45 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20 focus:ring-emerald-400/40'
                        "
                        :disabled="togglingId === row.id"
                        :aria-busy="togglingId === row.id"
                        @click="onToggleEnabled(row, !row.enabled)"
                      >
                        {{ row.enabled ? 'Inativar' : 'Ativar' }}
                      </button>
                    </div>
                  </td>
                  <td class="px-3 py-2 text-slate-300 max-w-[10rem] truncate font-medium" :title="row.agent.name">
                    {{ row.agent.name }}
                  </td>
                  <td class="px-3 py-2 text-slate-300">{{ specialtyLabel(row.agent.specialty) }}</td>
                  <td class="px-3 py-2">
                    <span :class="row.agent.active ? 'text-emerald-300/90' : 'text-slate-500'">
                      {{ row.agent.active ? 'Sim' : 'Não' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-slate-300 max-w-xs truncate" :title="row.description || '—'">
                    {{ row.description || '—' }}
                  </td>
                  <td class="px-3 py-2 text-slate-400 whitespace-nowrap">{{ scheduleSummary(row.schedule) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="pagy && pagy.count > 0"
            class="px-3 py-3 border-t border-slate-700 flex flex-wrap items-center gap-2 text-xs text-slate-400"
          >
            <span>{{ pagy.from }}–{{ pagy.to }} de {{ pagy.count }}</span>
            <button
              type="button"
              class="px-2 py-1 rounded border border-slate-600 disabled:opacity-40"
              :disabled="listLoading || !pagy.prev"
              @click="goPage(pagy.prev)"
            >
              Anterior
            </button>
            <span>Pág. {{ pagy.page }}/{{ pagy.pages }}</span>
            <button
              type="button"
              class="px-2 py-1 rounded border border-slate-600 disabled:opacity-40"
              :disabled="listLoading || !pagy.next"
              @click="goPage(pagy.next)"
            >
              Próxima
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi.js'

const LIST_ITEMS = 25

const SPECIALTY_LABELS = {
  data_analyst: 'Analista de dados',
  email_analyst: 'Analista de e-mail',
  record_creation: 'Criação de cadastros',
  knowledge_specialist: 'Especialista em conhecimento'
}

const WEEKDAY_LABELS = {
  0: 'Dom',
  1: 'Seg',
  2: 'Ter',
  3: 'Qua',
  4: 'Qui',
  5: 'Sex',
  6: 'Sáb'
}

export default {
  name: 'AgentSchedulesList',

  props: {
    tenantAdmin: {
      type: Boolean,
      default: true
    }
  },

  setup() {
    const { get, patch } = useApi()
    return { apiGet: get, apiPatch: patch }
  },

  data() {
    return {
      listLoading: true,
      loadError: '',
      rows: [],
      pagy: null,
      listPage: 1,
      togglingId: null,
      executorOptions: [],
      filters: {
        agentId: '',
        enabled: 'all'
      },
      scheduleFiltersOpen: false,
      scheduleFiltersMqDesktop:
        typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches
    }
  },

  computed: {
    scheduleFiltersExpanded() {
      return this.scheduleFiltersMqDesktop || this.scheduleFiltersOpen
    }
  },

  mounted() {
    this.syncScheduleFiltersBreakpoint()
    window.addEventListener('resize', this.syncScheduleFiltersBreakpoint)
    if (!this.tenantAdmin) {
      this.listLoading = false
      return
    }
    this.loadFilterOptions()
    this.fetchList()
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.syncScheduleFiltersBreakpoint)
  },

  methods: {
    syncScheduleFiltersBreakpoint() {
      if (typeof window === 'undefined') return
      const desktop = window.matchMedia('(min-width: 640px)').matches
      this.scheduleFiltersMqDesktop = desktop
      if (!desktop) this.scheduleFiltersOpen = false
    },

    specialtyLabel(s) {
      return SPECIALTY_LABELS[s] || s || '—'
    },

    scheduleSummary(s) {
      if (!s || !s.mode) return '—'
      if (s.mode === 'interval_minutes') {
        const n = s.every_minutes ?? 60
        return `A cada ${n} min`
      }
      if (s.mode === 'daily_at') {
        return `Diário às ${s.at_time || '—'}`
      }
      if (s.mode === 'weekly_at') {
        const days = Array.isArray(s.weekdays) ? s.weekdays : []
        const parts = days.map((d) => WEEKDAY_LABELS[d] || d).filter(Boolean)
        const dlabel = parts.length ? parts.join(', ') : '—'
        return `Semanal (${dlabel}) às ${s.at_time || '—'}`
      }
      return s.mode
    },

    buildListQuery(page) {
      const q = new URLSearchParams()
      q.set('items', String(LIST_ITEMS))
      q.set('page', String(page || 1))
      if (this.filters.agentId) q.set('agent_id', this.filters.agentId)
      if (this.filters.enabled && this.filters.enabled !== 'all') q.set('enabled', this.filters.enabled)
      return q.toString()
    },

    applyFilters() {
      this.listPage = 1
      this.fetchList()
      if (!this.scheduleFiltersMqDesktop) this.scheduleFiltersOpen = false
    },

    goPage(page) {
      if (page == null || page < 1) return
      this.listPage = page
      this.fetchList()
    },

    async loadFilterOptions() {
      const { data, error } = await this.apiGet('/api/v1/agents?agent_type=executor&items=200&page=1')
      if (!error && data?.data) {
        this.executorOptions = data.data.map((a) => ({ id: a.id, name: a.name }))
      }
    },

    async fetchList() {
      if (!this.tenantAdmin) return
      this.listLoading = true
      this.loadError = ''
      try {
        const qs = this.buildListQuery(this.listPage)
        const { data, error } = await this.apiGet(`/api/v1/agent_schedules?${qs}`)
        if (error) {
          const msg = error.response?.data?.errors
          this.loadError = Array.isArray(msg)
            ? msg.join(' ')
            : error.response?.data?.error || 'Não foi possível carregar os agendamentos.'
          this.rows = []
          this.pagy = null
          return
        }
        this.rows = Array.isArray(data?.data) ? data.data : []
        this.pagy = data?.pagy || null
        if (this.pagy?.page) this.listPage = this.pagy.page
      } finally {
        this.listLoading = false
      }
    },

    clearFilters() {
      this.filters = {
        agentId: '',
        enabled: 'all'
      }
      this.listPage = 1
      this.fetchList()
      if (!this.scheduleFiltersMqDesktop) this.scheduleFiltersOpen = false
    },

    async onToggleEnabled(row, enabled) {
      const prev = row.enabled
      row.enabled = enabled
      this.togglingId = row.id
      try {
        const { data, error } = await this.apiPatch(`/api/v1/agent_schedules/${row.id}`, {
          agent_schedule: { enabled }
        })
        if (error) {
          row.enabled = prev
          const msg = error.response?.data?.errors
          this.loadError = Array.isArray(msg)
            ? msg.join(' ')
            : error.response?.data?.error || 'Não foi possível atualizar.'
          return
        }
        if (data?.data) {
          const u = data.data
          row.enabled = u.enabled
          row.description = u.description
          row.schedule = u.schedule
        }
        this.loadError = ''
      } finally {
        this.togglingId = null
      }
    }
  }
}
</script>

<style scoped>
.agent-schedules-filter-control option {
  background-color: rgb(15 23 42);
  color: rgb(148 163 184);
}
</style>
